/**
 * The triage page: one episode, and everything needed to decide about it.
 *
 * `/argus/inbox/<id>` (or `/argus/history/<id>`), reached from a row of the
 * Inbox or History, or a bar on any timeline. Built around the plan's
 * *Suspicions and verdicts*: the sentence, the data around it on Explorer's
 * own chart, the timeline - this rule across its entities, or this entity
 * across its rules - what happened here before, the rule it came from, and the
 * way to the next one without going back to the list.
 *
 * Laid out as layout A1 (chosen 2026-09-27 from the sketch linked in
 * `todos/argus-roadmap.md`): the open list on the left, foldable with `[`;
 * the chart large in the middle; **Your call** at the top of a rail on the
 * right. A verdict (T2) is picked from the fixed list, given a reason and a
 * note, and **saved** - saving stays on the episode, and moving on is the
 * person's own step (J, or the list). An episode judged here stays in the list,
 * marked, until the page is left, so what was just done stays in view.
 *
 * A rule's related series and linked dashboard arrive in T3. Everything shown
 * was written by a watcher or said by a person; nothing is evaluated here
 * (rule 3).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import {
  addArgusNote,
  getArgusEpisode,
  judgeArgusEpisode,
  judgeArgusEpisodes,
  markArgusEpisodes,
  snoozeArgusEpisode,
  snoozeArgusEpisodes,
  takeArgusEpisode,
  takeArgusEpisodes,
} from '../../api/endpoints.ts';
import type {
  ArgusEpisodeDetail,
  ArgusInboxItem,
  ArgusTimelineBar,
  ArgusVerdictKey,
  ArgusVerdictOption,
} from '../../api/types.ts';
import { canDo, myUserId } from '../../state/store.ts';
import { authState } from '../../auth/auth.ts';
import { argusRoom, openArgusEpisode, setArgusRoom } from './argus-lens.ts';
import {
  inbox,
  inboxRuleFilter,
  loadAttention,
  loadInbox,
  markEpisode,
  rulesRoomOpenRule,
} from './argus-inbox-store.ts';
import { historyWords, severityOf, spanWords, verdictWords, when } from './argus-episodes.ts';
import { concluded, groupAlerts, shortSpan, type ArgusAlert } from './argus-alerts.ts';
import { renderTimeline } from './argus-timeline.ts';
import './argus-backtest-chart.ts';
import { userMessage } from '../../api/errors.ts';
import { showToast } from '../../ui/toast.ts';

const DAY = 86400000;

type Around = 'episode' | '7d' | '30d';

function naive(t: number): string {
  return new Date(t).toISOString().slice(0, 19);
}

function msOf(iso: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
}

/** "in 3 h", "in 2 days" - or "due now" once the moment has come. */
function until(iso: string): string {
  const minutes = Math.round((msOf(iso) - Date.now()) / 60000);
  if (minutes < 1) return 'due now';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} h`;
  return `in ${Math.round(hours / 24)} days`;
}

/**
 * Whether the open list is pinned open. Unpinned (the default since
 * 2026-10-22) it is a rail of dots that opens over the page on hover. A
 * per-browser convenience, never state that matters.
 */
const PIN_KEY = 'argus.triage.pinned';

/** "5 min ago", "3 h ago", "2 days ago". */
function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - msOf(iso)) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** The page's folding zones (2026-10-22, layout B): which are open. */
type ZoneKey = 'ships' | 'verdict' | 'timeline' | 'earlier' | 'notes' | 'rule';
const ZONES_KEY = 'argus.triage.zones';
/** The ships and the Verdict (chart and Your call) start open; the rest start folded. */
const ZONE_DEFAULTS: Record<ZoneKey, boolean> = {
  ships: true,
  verdict: true,
  timeline: false,
  earlier: false,
  notes: false,
  rule: false,
};

/** A table of more ships than this shows the first ones and a "Show more" row. */
const SHIP_ROWS = 8;

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {
    // A browser that keeps nothing forgets it after this visit.
  }
}

function readZones(): Record<ZoneKey, boolean> {
  try {
    const stored = JSON.parse(localStorage.getItem(ZONES_KEY) ?? '{}') as Partial<Record<ZoneKey, unknown>>;
    const zones = { ...ZONE_DEFAULTS };
    for (const key of Object.keys(zones) as ZoneKey[]) {
      if (typeof stored[key] === 'boolean') zones[key] = stored[key] as boolean;
    }
    return zones;
  } catch {
    return { ...ZONE_DEFAULTS };
  }
}

function writeZones(zones: Record<ZoneKey, boolean>): void {
  try {
    localStorage.setItem(ZONES_KEY, JSON.stringify(zones));
  } catch {
    // As above.
  }
}

/** A person icon and a tick, for Assign and Acknowledge. */
const ICON_PERSON = html`<svg class="argus-ep-ico" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.5" r="2.8" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M2.8 14c.6-2.8 2.7-4.2 5.2-4.2s4.6 1.4 5.2 4.2" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;
const ICON_TICK = html`<svg class="argus-ep-ico" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** Initials for the owner's round badge: "Elguja Lomsadze" is EL. */
function initials(name: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase();
}

/** The signed-in person's name, for a row changed before the server answers. */
function myName(): string | null {
  return authState.value.user?.name ?? null;
}

/** "Elguja Lomsadze" is "Elguja L.": a table cell has little room. */
function shortName(name: string | null): string {
  const parts = (name ?? 'Someone').trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]![0]}.` : parts[0]!;
}

/** A verdict's colour on this page: the same meaning the timeline gives it. */
const VERDICT_TONE: Record<ArgusVerdictKey, string> = {
  confirmed: 'is-critical',
  not_an_issue: 'is-ok',
  needs_info: 'is-info',
  duplicate: 'is-dup',
};

const STATE_WORDS: Record<string, string> = {
  open: 'still open',
  judged: 'judged',
  snoozed: 'muted',
  closed: 'ended',
  dismissed: 'dismissed',
  unwatched: 'no longer watched',
};

@customElement('argus-episode-page')
export class ArgusEpisodePage extends LightElement {
  @property({ type: Number }) episodeId = 0;

  @state() private detail: ArgusEpisodeDetail | null = null;
  @state() private failed: string | null = null;
  @state() private around: Around = 'episode';
  @state() private view: 'rule' | 'entity' = 'rule';
  @state() private busy = false;
  @state() private actionError: string | null = null;
  @state() private pinned = readFlag(PIN_KEY);
  @state() private zones = readZones();
  /** The ship table shows every ship, not only the first `SHIP_ROWS`. */
  @state() private allShips = false;
  /** The alert `allShips` belongs to: another alert starts short again. */
  private shipsAlert = '';
  /** Assign all to me met ships a colleague has: the question is showing. */
  @state() private askTakeAll = false;
  /** The ship row whose ··· menu is open. */
  @state() private rowMenu: number | null = null;
  /**
   * What a ship-table action changed, shown at once while the server saves it
   * and the page reads it again - so a click changes one cell, and nothing
   * dims or jumps. Dropped when the fresh copy has arrived.
   */
  @state() private patches: ReadonlyMap<number, Partial<ArgusInboxItem>> = new Map();
  /** The verdict being written: null until one is picked. */
  @state() private pick: ArgusVerdictKey | null = null;
  @state() private reason: string | null = null;
  @state() private note = '';
  /** Showing the saved verdict, rather than the form to change it. */
  @state() private showSaved = false;
  @state() private snoozeOpen = false;
  /**
   * Ships ticked in the ship table (2026-10-05, layout A). While any are, the
   * table's bar and Your call act on them together and the chart draws them
   * all; with none, everything acts on the ship being looked at.
   */
  @state() private selected: ReadonlySet<number> = new Set();
  /** The alert the selection belongs to: moving to another alert clears it. */
  private selectedAlert = '';
  @state() private bulkSnoozeOpen = false;
  /** Judged on this visit: kept in the list, marked, until the page is left. */
  private kept = new Map<number, ArgusInboxItem>();
  /**
   * Episodes read on this visit. Moving to one already read shows it at once
   * (and reads it again behind); moving to a new one keeps the page as it is,
   * dimmed under a progress bar, until it arrives - never an empty page.
   */
  private cache = new Map<number, ArgusEpisodeDetail>();
  private loadedId = 0;
  private unsubscribe: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('pointerdown', this.onOutside, true);
    this.unsubscribe = inbox.subscribe(() => this.requestUpdate());
    void loadInbox();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onOutside, true);
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.kept.clear();
    this.cache.clear();
  }

  protected override willUpdate(): void {
    if (this.episodeId && this.episodeId !== this.loadedId) {
      this.loadedId = this.episodeId;
      const known = this.cache.get(this.episodeId);
      if (known) {
        this.detail = known;
        this.startForm(known.episode);
      }
      this.failed = null;
      this.around = 'episode';
      this.actionError = null;
      this.snoozeOpen = false;
      this.bulkSnoozeOpen = false;
      this.rowMenu = null;
      this.askTakeAll = false;
      if (this.selectedAlert && this.currentAlert()?.key !== this.selectedAlert) {
        this.selectedAlert = '';
        this.selected = new Set();
      }
      const alertKey = this.currentAlert()?.key ?? '';
      if (alertKey !== this.shipsAlert) {
        this.shipsAlert = alertKey;
        this.allShips = false;
      }
      void this.load(this.episodeId);
    }
  }

  private async load(id: number): Promise<void> {
    try {
      const detail = await getArgusEpisode(id);
      this.cache.set(id, detail);
      if (id !== this.episodeId) return;
      const fresh = this.detail?.episode.id !== id;
      this.detail = detail;
      if (fresh) this.startForm(detail.episode);
      this.prefetch();
    } catch (error) {
      if (id === this.episodeId) this.failed = userMessage(error, 'This alert could not be read.');
    }
  }

  /** Read the other ships of this alert ahead, so moving between them is instant. */
  private prefetch(): void {
    const alert = this.currentAlert();
    if (!alert) return;
    for (const i of alert.items.slice(0, 12)) {
      if (i.id === this.episodeId || this.cache.has(i.id)) continue;
      void getArgusEpisode(i.id).then(
        (d) => this.cache.set(i.id, d),
        () => undefined,
      );
    }
  }

  /** The page still shows the episode left behind while the next one is read. */
  private get stale(): boolean {
    return !!this.detail && this.detail.episode.id !== this.episodeId;
  }

  /** No action while one is saving, or while the page shows another episode than the address. */
  private get locked(): boolean {
    return this.busy || this.stale;
  }

  /** The form starts from what was said before, and shows it if anything was. */
  private startForm(e: ArgusInboxItem): void {
    this.pick = e.verdict;
    this.reason = e.verdict_reason;
    this.note = e.verdict_note ?? '';
    this.showSaved = e.verdict !== null;
  }

  /**
   * The open list: what the Inbox holds, plus what was judged on this visit,
   * newest first - so J / K and the list agree, and a just-judged episode
   * does not jump out from under the pointer.
   */
  private rows(): ArgusInboxItem[] {
    const open = inbox.value?.open ?? [];
    const ids = new Set(open.map((r) => r.id));
    const kept = [...this.kept.values()].filter((r) => !ids.has(r.id));
    return [...open, ...kept].sort((a, b) => msOf(b.started_at) - msOf(a.started_at));
  }

  /** The open list as alerts - one per rule, its ships inside (2026-09-27). */
  private alerts(): ArgusAlert[] {
    return groupAlerts(this.rows());
  }

  private currentAlert(): ArgusAlert | null {
    return this.alerts().find((a) => a.items.some((i) => i.id === this.episodeId)) ?? null;
  }

  /** J / K: the next or previous alert, at its first ship still waiting. */
  private step(by: 1 | -1): void {
    const alerts = this.alerts();
    const at = alerts.findIndex((a) => a.items.some((i) => i.id === this.episodeId));
    const next = at < 0 ? alerts[0] : alerts[at + by];
    if (next) openArgusEpisode(this.firstOf(next).id);
  }

  private firstOf(alert: ArgusAlert): ArgusInboxItem {
    return alert.items.find((i) => !concluded(i)) ?? alert.items[0]!;
  }

  /** The ticked ships still in this alert, in the table's order. */
  private selectedItems(): ArgusInboxItem[] {
    const alert = this.currentAlert();
    return alert ? alert.items.filter((i) => this.selected.has(i.id)) : [];
  }

  /** What an action acts on: the ticked ships, or else the ship being looked at. */
  private targetIds(): number[] {
    const picked = this.selectedItems().map((i) => i.id);
    return picked.length ? picked : [this.episodeId];
  }

  private toggle(id: number): void {
    const next = new Set(this.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.selected = next;
    this.selectedAlert = this.currentAlert()?.key ?? '';
    this.bulkSnoozeOpen = false;
  }

  /** The table's head box: tick every ship still under review, or clear. */
  private toggleAll(): void {
    const alert = this.currentAlert();
    if (!alert) return;
    const open = alert.items.filter((i) => !concluded(i)).map((i) => i.id);
    const all = open.length > 0 && open.every((id) => this.selected.has(id));
    this.selected = all ? new Set() : new Set(open);
    this.selectedAlert = alert.key;
    this.bulkSnoozeOpen = false;
  }

  private options(): ArgusVerdictOption[] {
    return this.detail?.verdicts ?? [];
  }

  private choose(key: ArgusVerdictKey): void {
    this.actionError = null;
    this.showSaved = false;
    if (this.pick === key) return;
    this.pick = key;
    const reasons = this.options().find((v) => v.key === key)?.reasons ?? [];
    // One reason is no choice: Duplicate has only "of another suspicion".
    this.reason = reasons.length === 1 ? (reasons[0]?.key ?? null) : null;
  }

  private canSave(): boolean {
    return !!this.pick && !!this.reason && !this.locked && this.mayJudge();
  }

  /**
   * J / K (or ← / →) move through the open list, as a mail client does; 1-4
   * pick a verdict; Enter saves it; [ folds the list; Esc goes back.
   */
  private onKey = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || e.metaKey || e.altKey) return;
    // The target may be the document itself, which has no `closest`.
    const target = e.target instanceof Element ? e.target : null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
      if (e.key === 'Enter' && e.ctrlKey && target.closest('.argus-ep-note') && this.canSave()) {
        e.preventDefault();
        void this.save();
      }
      return;
    }
    if (e.ctrlKey || !this.detail) return;
    const options = this.options();
    if (e.key === 'j' || e.key === 'ArrowRight') this.step(1);
    else if (e.key === 'k' || e.key === 'ArrowLeft') this.step(-1);
    else if (e.key === 'Escape') openArgusEpisode(null);
    else if (e.key === '[') this.fold();
    else if (e.key === 'x' && (this.currentAlert()?.items.length ?? 0) > 1) this.toggle(this.episodeId);
    else if (/^[1-9]$/.test(e.key) && this.mayJudge() && options[Number(e.key) - 1]) {
      this.choose(options[Number(e.key) - 1]!.key);
    } else if (e.key === 'Enter' && !target?.closest('button, a') && this.canSave() && !this.showSaved) {
      void this.save();
    } else return;
    e.preventDefault();
  };

  /** A click outside a Mute menu closes it. */
  private onOutside = (e: Event): void => {
    if (this.rowMenu === null && !this.snoozeOpen && !this.bulkSnoozeOpen) return;
    if (e.target instanceof Element && e.target.closest('.argus-ep-snooze')) return;
    this.rowMenu = null;
    this.snoozeOpen = false;
    this.bulkSnoozeOpen = false;
  };

  /** Pin the open list open, or let it fold back to its rail. */
  private fold(): void {
    this.pinned = !this.pinned;
    writeFlag(PIN_KEY, this.pinned);
  }

  private toggleZone(key: ZoneKey): void {
    this.zones = { ...this.zones, [key]: !this.zones[key] };
    writeZones(this.zones);
  }

  /**
   * A folding zone, as in the rule editor (`.argus-e-zone`): a caret, a name,
   * and a one-line summary while it is folded. `tools` sit beside the toggle,
   * never inside it; `alwaysTools` keeps them there when it is folded too.
   */
  private zone(
    key: ZoneKey,
    name: string,
    summary: string,
    body: TemplateResult | typeof nothing,
    opts: { tools?: TemplateResult | typeof nothing; alwaysTools?: boolean; docked?: boolean; extra?: TemplateResult | typeof nothing; keepSummary?: boolean; cls?: string } = {},
  ): TemplateResult {
    const open = this.zones[key];
    return html`<section class="argus-e-zone is-fold argus-ep-zone ${open ? 'is-open' : ''} ${opts.docked ? 'is-docked' : ''} ${opts.cls ?? ''}" aria-label=${name}>
      <div class="argus-e-zone-h argus-ep-zone-h">
        <button type="button" class="argus-e-zone-toggle" aria-expanded=${open ? 'true' : 'false'} @click=${() => this.toggleZone(key)}>
          <span class="argus-e-zone-caret" aria-hidden="true"></span>
          <span class="argus-e-zone-name">${name}</span>
          ${opts.docked ? html`<span class="argus-ep-docked" title="Open by default">Docked</span>` : nothing}
          ${opts.extra ?? nothing}
          <span class="argus-e-zone-sum ${opts.keepSummary ? 'is-kept' : ''}">${summary}</span>
        </button>
        ${opts.tools && (open || opts.alwaysTools) ? html`<span class="argus-ep-ztools">${opts.tools}</span>` : nothing}
      </div>
      <div class="argus-e-zone-b" ?hidden=${!open}>${body}</div>
    </section>`;
  }

  /** Run one triage write, then read the page, the list and the count again. */
  private async act(write: () => Promise<unknown>): Promise<boolean> {
    this.busy = true;
    this.actionError = null;
    try {
      await write();
    } catch (error) {
      this.actionError = userMessage(error, 'That could not be saved.');
      this.busy = false;
      return false;
    }
    // What was read ahead may be out of date now (a verdict for all ships, an assignment).
    this.cache.clear();
    await Promise.all([this.load(this.episodeId), loadInbox(), loadAttention()]);
    this.busy = false;
    return true;
  }

  /**
   * A ship-table action: the rows change at once (`patch`), the write runs, and
   * the page and the Inbox are read again behind it. Unlike `act` nothing is
   * locked or dimmed meanwhile - a row is its own thing.
   */
  private async quickAct(ids: number[], write: () => Promise<unknown>, patch?: Partial<ArgusInboxItem>): Promise<void> {
    if (!ids.length) return;
    this.actionError = null;
    const set = (on: boolean): void => {
      const next = new Map(this.patches);
      for (const id of ids) {
        if (on && patch) next.set(id, { ...next.get(id), ...patch });
        else next.delete(id);
      }
      this.patches = next;
    };
    set(true);
    try {
      await write();
    } catch (error) {
      set(false);
      this.actionError = userMessage(error, 'That could not be saved.');
      return;
    }
    this.cache.clear();
    await Promise.all([this.load(this.episodeId), loadInbox(), loadAttention()]);
    set(false);
  }

  /**
   * The alert's ships for the table: in one fixed order (when each started,
   * then the name) - never by state, so acknowledging a ship does not move
   * its row - with any change not saved yet laid over them.
   */
  private shipItems(alert: ArgusAlert): ArgusInboxItem[] {
    return [...alert.items]
      .sort((a, b) => msOf(a.started_at) - msOf(b.started_at) || a.entity_name.localeCompare(b.entity_name))
      .map((i) => {
        const patch = this.patches.get(i.id);
        return patch ? { ...i, ...patch } : i;
      });
  }

  private async save(): Promise<void> {
    if (!this.canSave()) return;
    const id = this.episodeId;
    const note = this.note.trim() || null;
    const ids = this.targetIds();
    // Keep every ship this verdict covers in the list until the page is left.
    const before = new Map(this.rows().map((r) => [r.id, r]));
    const write = ids.length > 1
      ? () => judgeArgusEpisodes(ids, this.pick, this.reason, note)
      : () => judgeArgusEpisode(id, this.pick, this.reason, note);
    if (await this.act(write)) {
      for (const each of ids) {
        const row = each === id ? this.detail?.episode : before.get(each);
        if (row) {
          this.kept.set(each, {
            ...row,
            verdict: this.pick,
            verdict_reason: this.reason,
            state: this.pick === 'needs_info' ? row.state : 'judged',
          });
        }
      }
      if (this.detail?.episode.id === id) this.kept.set(id, this.detail.episode);
      // A verdict for the ticked ships ends that selection: they are decided.
      this.selected = new Set();
      if (ids.includes(id)) this.showSaved = true;
      else if (this.detail) this.startForm(this.detail.episode);
    }
  }

  private async unjudge(): Promise<void> {
    const id = this.episodeId;
    if (await this.act(() => judgeArgusEpisode(id, null, null, null))) {
      this.kept.delete(id);
      if (this.detail) this.startForm(this.detail.episode);
    }
  }

  private async mark(mark: 'seen' | 'dismissed' | null): Promise<void> {
    this.busy = true;
    this.actionError = await markEpisode(this.episodeId, mark);
    this.busy = false;
    if (!this.actionError) {
      this.cache.delete(this.episodeId);
      await this.load(this.episodeId);
    }
  }

  private openRule(): void {
    if (!this.detail) return;
    rulesRoomOpenRule.set(this.detail.rule.id);
    setArgusRoom('rules');
  }

  private range(d: ArgusEpisodeDetail): { from: string; to: string } {
    const start = msOf(d.episode.started_at);
    const end = msOf(d.episode.ended_at ?? d.episode.last_at ?? d.episode.started_at);
    if (this.around === '7d') return { from: naive(end - 7 * DAY), to: naive(Math.max(end, start) + DAY) };
    if (this.around === '30d') return { from: naive(end - 30 * DAY), to: naive(end + DAY) };
    // A day either side, or the episode's own length when that is longer.
    const pad = Math.max(DAY, end - start);
    return { from: naive(start - pad), to: naive(end + pad) };
  }

  private renderHead(d: ArgusEpisodeDetail): TemplateResult {
    const e = d.episode;
    const severity = severityOf(e);
    const open = e.state === 'open';
    const alerts = this.alerts();
    const at = alerts.findIndex((a) => a.items.some((i) => i.id === e.id));
    const back = argusRoom.value === 'history' ? 'History' : 'Inbox';
    return html`
      <nav class="argus-ep-nav" aria-label="Alerts">
        <button type="button" class="argus-lmore" @click=${() => openArgusEpisode(null)}>← ${back}</button>
        <span class="argus-ep-nav-right">
          ${at >= 0
            ? html`<span class="argus-lfaint">alert ${at + 1} of ${alerts.length}</span>`
            : html`<span class="argus-lfaint">not in the open list</span>`}
          <button type="button" class="dash-btn" ?disabled=${at <= 0} title="Previous alert (K)" @click=${() => this.step(-1)}>‹</button>
          <button type="button" class="dash-btn" ?disabled=${at < 0 || at === alerts.length - 1} title="Next alert (J)" @click=${() => this.step(1)}>›</button>
        </span>
      </nav>
      <header class="argus-ep-head ${open ? `glow-row is-${severity} ${e.mark === 'seen' ? 'is-seen' : 'glow-alive'}` : ''}">
        <div class="argus-ep-kicker">
          <span class="argus-chip is-${severity}">${severity}</span>
          <span>${e.verdict
            ? html`<span class="argus-ep-verdict-word ${VERDICT_TONE[e.verdict]}">${verdictWords(e)}</span>`
            : open
              ? 'Suspected anomaly - please review'
              : STATE_WORDS[e.state] ?? e.state}</span>
          ${e.owner_name ? html`<span class="argus-ibx-seen">${e.owner_id === myUserId.value ? 'assigned to you' : `${e.owner_name} assigned`}</span>` : nothing}
          ${e.mark === 'seen' && open ? html`<span class="argus-ibx-seen">acknowledged by ${e.marked_by_name ?? 'someone'}</span>` : nothing}
        </div>
        <h2 class="argus-ep-title" title=${`${e.rule_name} on ${e.entity_name}`}>${e.rule_name} <span class="argus-ep-on">on</span> ${e.entity_name}</h2>
        ${e.sentence ? html`<p class="argus-ep-sentence" title=${e.sentence}>${e.sentence}</p>` : nothing}
        <p class="argus-lfaint argus-ep-meta">
          <span>${open ? 'Since' : 'From'} ${when(e.started_at)}</span>
          <span>${spanWords(e.started_at, e.ended_at ?? e.last_at)}${open ? ' so far' : ''}</span>
          ${open || e.state === 'judged' ? nothing : html`<span>${historyWords(e)}</span>`}
          <span title=${d.checked_at ? `Checked ${when(d.checked_at)}` : ''}>
            ${d.data_through ? `data through ${when(d.data_through)}` : 'no evaluation on record'}
          </span>
        </p>
      </header>
    `;
  }

  private renderData(d: ArgusEpisodeDetail): TemplateResult {
    const range = this.range(d);
    const here = d.timeline.by_rule.find((l) => l.key === d.episode.entity)?.bars ?? [];
    const episodes = here.map((b) => ({
      entity: b.entity,
      start: naive(msOf(b.start)),
      end: b.end ? naive(msOf(b.end)) : null,
      last: naive(msOf(b.last)),
      steps: 0,
    }));
    return html`${d.chart
        ? html`<argus-backtest-chart
            .spec=${d.chart}
            .episodes=${episodes}
            .entity=${d.episode.entity}
            .also=${this.selectedItems().map((i) => i.entity)}
            .from=${range.from}
            .to=${range.to}
            .source=${d.episode.source_key}
            .ruleId=${d.rule.id}
            .charts=${d.rule.charts ?? []}
          ></argus-backtest-chart>
          <p class="argus-lfaint">
            Shaded: every time this rule fired on ${d.episode.entity_name}. Dashed: the rule's own numbers.
            ${d.rule.found_this_version ? '' : html`<b>Found by version ${d.episode.version};</b> the rule has changed since.`}
          </p>`
        : html`<p class="argus-lfaint">This rule's series cannot be drawn.</p>`}`;
  }

  /** The chart's window and the way to Explore, in the zone's head. */
  private dataTools(d: ArgusEpisodeDetail): TemplateResult {
    const choice = (value: Around, label: string): TemplateResult =>
      html`<button type="button" class="argus-ep-seg ${this.around === value ? 'is-on' : ''}" aria-pressed=${this.around === value ? 'true' : 'false'} @click=${() => (this.around = value)}>${label}</button>`;
    return html`<span class="argus-ep-segs" role="group" aria-label="Window">
        ${choice('episode', 'Around it')} ${choice('7d', '7 days')} ${choice('30d', '30 days')}
      </span>
      ${d.explore_path
        ? html`<a class="argus-lmore argus-ep-explore" href=${d.explore_path} title="The same series, window and ship in Explore">Open in Explore →</a>`
        : nothing}`;
  }

  private timelineTools(d: ArgusEpisodeDetail): TemplateResult {
    const byRule = this.view === 'rule';
    return html`<span class="argus-ep-segs" role="group" aria-label="Timeline">
      <button type="button" class="argus-ep-seg ${byRule ? 'is-on' : ''}" aria-pressed=${byRule ? 'true' : 'false'} @click=${() => (this.view = 'rule')}>
        This rule, every ${d.episode.source_key === 'historical' ? 'ship' : 'entity'}
      </button>
      <button type="button" class="argus-ep-seg ${byRule ? '' : 'is-on'}" aria-pressed=${byRule ? 'false' : 'true'} @click=${() => (this.view = 'entity')}>
        ${d.episode.entity_name}, every rule
      </button>
    </span>`;
  }

  private renderTimelineCard(d: ArgusEpisodeDetail): TemplateResult {
    const byRule = this.view === 'rule';
    const lanes = byRule ? d.timeline.by_rule : d.timeline.by_entity;
    const pick = (bar: ArgusTimelineBar): void => {
      if (bar.id !== this.episodeId) openArgusEpisode(bar.id);
    };
    return html`${renderTimeline(lanes, {
        days: d.timeline.days,
        highlight: this.episodeId,
        highlightLane: byRule ? d.episode.entity : d.rule.id,
        onPick: pick,
        quiet: byRule ? 'nothing found' : 'nothing',
      })}
      <p class="argus-lfaint">Click a bar to open it.</p>`;
  }

  private renderEarlier(d: ArgusEpisodeDetail): TemplateResult {
    return html`${d.earlier.length
        ? html`<ul class="argus-ep-earlier">
            ${d.earlier.map(
              (e: ArgusInboxItem) => html`<li>
                <button type="button" class="argus-ep-earlier-row" @click=${() => openArgusEpisode(e.id)}>
                  <span>${when(e.started_at)}</span>
                  <span>${spanWords(e.started_at, e.ended_at ?? e.last_at)}</span>
                  <span class="argus-lfaint">${e.state === 'open' ? 'still open' : historyWords(e)}</span>
                </button>
              </li>`,
            )}
          </ul>`
        : html`<p class="argus-lfaint">The first time this rule has found anything on ${d.episode.entity_name} in ${d.timeline.days} days.</p>`}`;
  }

  /**
   * May this person judge, take, mark and mute this alert? Its team may (and
   * root and administrators); a follower reads it and adds notes (decision 35).
   * The database says the same, whatever this answers.
   */
  private mayJudge(): boolean {
    return canDo('argus:triage') && this.detail?.episode.may_handle !== false;
  }

  @state() private noteDraft = '';
  @state() private noteBusy = false;

  private async addNote(d: ArgusEpisodeDetail): Promise<void> {
    const body = this.noteDraft.trim();
    if (!body || this.noteBusy) return;
    this.noteBusy = true;
    try {
      const answer = await addArgusNote(d.episode.id, body);
      if (this.detail?.episode.id === d.episode.id) this.detail = { ...this.detail, notes: answer.notes };
      this.noteDraft = '';
    } catch (error) {
      showToast('error', userMessage(error, 'The note was not added.'), 0);
    } finally {
      this.noteBusy = false;
    }
  }

  /** Notes on the alert: the team's and the followers', oldest first. Not verdicts. */
  private renderNotes(d: ArgusEpisodeDetail): TemplateResult {
    const notes = d.notes ?? [];
    return html`<div class="argus-ep-notes">
      ${notes.length
        ? html`<ol class="argus-ep-notelist">
            ${notes.map(
              (n) => html`<li>
                <p>${n.body}</p>
                <span class="argus-lfaint">${n.author_name}${n.by_team ? '' : ' · follower'} · ${when(n.created_at)}</span>
              </li>`,
            )}
          </ol>`
        : html`<p class="argus-lfaint">No notes yet.</p>`}
      <textarea
        class="argus-ep-note-in"
        rows="2"
        maxlength="2000"
        placeholder="Add a note - what you know about this"
        aria-label="Add a note"
        .value=${this.noteDraft}
        @input=${(e: Event) => (this.noteDraft = (e.target as HTMLTextAreaElement).value)}
        @keydown=${(e: KeyboardEvent) => {
          e.stopPropagation();
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void this.addNote(d);
        }}
      ></textarea>
      <div class="argus-ep-actions">
        <button type="button" class="dash-btn" ?disabled=${!this.noteDraft.trim() || this.noteBusy} @click=${() => void this.addNote(d)}>
          Add note
        </button>
      </div>
    </div>`;
  }

  private renderJudge(d: ArgusEpisodeDetail): TemplateResult {
    const e = d.episode;
    const may = this.mayJudge();
    const error = this.actionError ? html`<p class="argus-form-error" role="alert">${this.actionError}</p>` : nothing;
    if (!may) {
      const follower = e.may_handle === false;
      return html`<div class="argus-ep-judge">
        ${e.verdict ? html`<p class="argus-ep-saved ${VERDICT_TONE[e.verdict]}"><b>${verdictWords(e)}</b></p>` : nothing}
        <p class="argus-lfaint">
          ${follower
            ? `${e.group_name ?? 'Its team'} judges and closes this alert. You can add a note below.`
            : 'Read only: judging needs the Argus Operator role.'}
        </p>
      </div>`;
    }
    if (this.showSaved && e.verdict) {
      return html`<div class="argus-ep-judge">
        <div class="argus-ep-saved ${VERDICT_TONE[e.verdict]}">
          <b>${verdictWords(e)}</b>
          ${e.verdict_note ? html`<span class="argus-ep-saved-note">${e.verdict_note}</span>` : nothing}
          <span class="argus-lfaint">
            ${e.judged_by_name ?? 'Someone'}${e.judged_at ? `, ${when(e.judged_at)}` : ''}${e.verdict === 'needs_info'
              ? ' · stays open until answered'
              : ' · closed; it leaves the Inbox when you move on'}
          </span>
        </div>
        <div class="argus-ep-actions">
          <button type="button" class="dash-btn" ?disabled=${this.locked} @click=${() => (this.showSaved = false)}>Edit verdict</button>
          <button type="button" class="dash-btn" ?disabled=${this.locked} title="Takes the verdict off and puts the alert back in the Inbox, if it is still going" @click=${() => void this.unjudge()}>Reopen</button>
          <button type="button" class="dash-btn" @click=${() => this.step(1)}>Next <span class="argus-kbd">J</span></button>
        </div>
        ${error}
      </div>`;
    }
    const options = this.options();
    const picked = options.find((v) => v.key === this.pick);
    // Every part has a fixed place: picking a verdict fills the reason grid,
    // it never adds or removes a row, so nothing below moves.
    const REASON_SLOTS = 6;
    const reasons = picked?.reasons ?? [];
    return html`<div class="argus-ep-judge is-deciding">
      ${e.verdict
        ? html`<button type="button" class="argus-lmore argus-ep-cancel-edit" title=${`Keep ${verdictWords(e)}`} @click=${() => this.startForm(e)}>Cancel edit</button>`
        : nothing}
      <div class="argus-ep-field">
        <span class="argus-ep-note-label">Verdict</span>
        <div class="argus-ep-verdicts" role="radiogroup" aria-label="Verdict">
          ${options.map(
            (v, i) => html`<button
              type="button"
              role="radio"
              class="argus-ep-verdict ${VERDICT_TONE[v.key]}"
              aria-checked=${this.pick === v.key ? 'true' : 'false'}
              ?disabled=${this.locked}
              @click=${() => this.choose(v.key)}
            >
              <span class="argus-ep-verdict-dot" aria-hidden="true"></span>
              <span class="argus-ep-verdict-text"><b>${v.label}</b><small>${v.hint}</small></span>
              <span class="argus-kbd" aria-hidden="true">${i + 1}</span>
            </button>`,
          )}
        </div>
      </div>
      <div class="argus-ep-field">
        <span class="argus-ep-note-label">Reason</span>
        <div class="argus-ep-reasons ${picked ? '' : 'is-waiting'}" role="radiogroup" aria-label="Reason">
          ${picked
            ? [
                ...reasons.map(
                  (r) => html`<button
                    type="button"
                    role="radio"
                    class="argus-ep-reason"
                    aria-checked=${this.reason === r.key ? 'true' : 'false'}
                    @click=${() => (this.reason = r.key)}
                  >
                    ${r.label}
                  </button>`,
                ),
                ...Array.from(
                  { length: Math.max(0, REASON_SLOTS - reasons.length) },
                  () => html`<span class="argus-ep-reason is-empty" aria-hidden="true"></span>`,
                ),
              ]
            : html`<span class="argus-ep-reasons-hint">Pick a verdict to see its reasons</span>`}
        </div>
      </div>
      <label class="argus-ep-note-field">
        <span class="argus-ep-note-label">Note <span>optional · Ctrl+Enter saves</span></span>
        <textarea
          class="argus-ep-note"
          rows="3"
          maxlength="2000"
          placeholder="What you checked, who you asked, what to watch for next time"
          .value=${this.note}
          @input=${(ev: Event) => (this.note = (ev.target as HTMLTextAreaElement).value)}
        ></textarea>
      </label>
      <button
        type="button"
        class="argus-ep-save"
        ?disabled=${!this.canSave()}
        title=${!this.pick ? 'Pick a verdict first' : !this.reason ? 'Pick a reason first' : ''}
        @click=${() => void this.save()}
      >
        ${this.busy
          ? 'Saving…'
          : this.pick === 'needs_info'
            ? 'Save, keep open'
            : this.targetIds().length > 1
              ? `Close alert for ${this.targetIds().length} ${this.currentAlert()?.noun ?? 'ship'}s`
              : 'Close alert'}
        <span class="argus-kbd">Enter</span>
      </button>
      ${error}
    </div>`;
  }

  /** A Mute button with its menu of days, for one ship, a row or the ticked ships. */
  private muteButton(
    label: string,
    title: string,
    open: boolean,
    toggle: () => void,
    pick: (days: number) => void,
    cls = 'argus-ep-qa is-ghost',
  ): TemplateResult {
    return html`<span class="argus-ep-snooze">
      <button type="button" class=${cls} ?disabled=${this.locked} aria-expanded=${open ? 'true' : 'false'} title=${title} @click=${toggle}>
        ${label}
      </button>
      ${open
        ? html`<span class="cat-menu argus-ep-snooze-menu" role="menu">
            ${(this.detail?.snooze_days ?? []).map(
              (n) => html`<button class="cat-menu-item" role="menuitem" type="button" @click=${() => pick(n)}>
                ${n === 1 ? '1 day' : `${n} days`}
              </button>`,
            )}
          </span>`
        : nothing}
    </span>`;
  }

  /**
   * An alert on one ship: who has it, whether it is acknowledged or muted, and
   * the three buttons, on one line in the place the ship table takes for an
   * alert on several. The buttons change their words, never their place.
   */
  private renderHandling(d: ArgusEpisodeDetail): TemplateResult | typeof nothing {
    if (!this.mayJudge()) return nothing;
    const e = d.episode;
    const mine = e.owner_id !== null && e.owner_id === myUserId.value;
    const open = e.state === 'open';
    const status: string[] = [];
    status.push(e.owner_name ? (mine ? 'Assigned to you' : `Assigned to ${e.owner_name}`) : 'Unassigned');
    if (e.mark === 'seen') status.push(`acknowledged by ${e.marked_by_name ?? 'someone'}`);
    if (e.mark === 'dismissed') status.push('dismissed');
    if (e.snoozed_until) status.push(`muted until ${when(e.snoozed_until)}`);
    const assign = mine
      ? html`<button type="button" class="argus-ep-qa is-assign" ?disabled=${this.locked} title="Take your name off it, so someone else can pick it up" @click=${() => void this.act(() => takeArgusEpisode(e.id, false))}>${ICON_PERSON}Unassign</button>`
      : html`<button
          type="button"
          class="argus-ep-qa is-assign"
          ?disabled=${this.locked}
          title=${e.owner_name
            ? `${e.owner_name} is looking into this. Reassign it to put your name on it instead.`
            : 'Put your name on it, so nobody else digs into the same thing. Closing the alert unassigns it.'}
          @click=${() => void this.act(() => takeArgusEpisode(e.id, true))}
        >${ICON_PERSON}${e.owner_name ? 'Reassign to me' : 'Assign to me'}</button>`;
    const ack =
      e.mark === 'dismissed'
        ? html`<button type="button" class="argus-ep-qa is-ack" ?disabled=${this.locked} @click=${() => void this.mark(null)}>${ICON_TICK}Undo dismiss</button>`
        : e.mark === 'seen'
          ? html`<button type="button" class="argus-ep-qa is-ack" ?disabled=${this.locked} @click=${() => void this.mark(null)}>${ICON_TICK}Undo acknowledge</button>`
          : html`<button
              type="button"
              class="argus-ep-qa is-ack"
              ?disabled=${this.locked || !open}
              title=${open
                ? 'Say the team has seen it: the glow stops for everyone and it leaves the count, but it stays in the Inbox until it gets a verdict or ends'
                : 'Only an alert still open can be acknowledged'}
              @click=${() => void this.mark('seen')}
            >${ICON_TICK}Acknowledge</button>`;
    const mute = e.snoozed_until
      ? html`<button type="button" class="argus-ep-qa is-ghost" ?disabled=${this.locked} title=${`This rule is muted on ${e.entity_name} until ${when(e.snoozed_until)}. Unmute puts it back in the Inbox now.`} @click=${() => void this.act(() => snoozeArgusEpisode(e.id, null))}>Unmute</button>`
      : this.muteButton(
          `Mute on ${e.entity_name}…`,
          `Keep this rule out of the Inbox and the count on ${e.entity_name} for a while - for a known issue being fixed. The watcher keeps running and recording; only the alerting goes quiet.`,
          this.snoozeOpen,
          () => (this.snoozeOpen = !this.snoozeOpen),
          (n) => {
            this.snoozeOpen = false;
            void this.act(() => snoozeArgusEpisode(e.id, n));
          },
        );
    return html`<div class="argus-ep-hline">
      <span class="argus-ep-hline-who"><b>${e.entity_name}</b><small>${status.join(' · ')}</small></span>
      ${assign} ${ack} ${mute}
    </div>`;
  }

  /** The open alerts: a rail of dots that opens over the page on hover, or pinned open. */
  private renderQueue(): TemplateResult {
    const alerts = this.alerts();
    const filter = inboxRuleFilter.value;
    const here = (a: ArgusAlert): boolean => a.items.some((i) => i.id === this.episodeId);
    const done = (a: ArgusAlert): boolean => a.items.every(concluded);
    const waiting = alerts.filter((a) => !done(a)).length;
    const list = html`<div class="argus-ep-queue-head">
        <h3>Open alerts <span class="argus-lfaint">${waiting}</span></h3>
        <button type="button" class="argus-ep-pin ${this.pinned ? 'is-on' : ''}" aria-pressed=${this.pinned ? 'true' : 'false'} title=${this.pinned ? 'Fold the list back to its rail ([)' : 'Keep the list open ([)'} @click=${() => this.fold()}>
          ${this.pinned ? 'Unpin' : 'Pin'}
        </button>
      </div>
      ${filter ? html`<p class="argus-lfaint argus-ep-queue-note">${filter.name} only</p>` : nothing}
      <div class="argus-ep-queue-list" role="list">
        ${alerts.length
          ? alerts.map((a) => {
              const judged = done(a);
              const n = a.items.length;
              const sub = judged
                ? verdictWords(a.items[0]!)
                : n === 1
                  ? `${a.items[0]!.entity_name} · ${when(a.items[0]!.started_at)}`
                  : `${n}${a.scope != null ? `/${a.scope}` : ''} ${a.noun}s${a.pending.length && a.pending.length < n ? ` · ${a.pending.length} new` : ''}`;
              return html`<button
                type="button"
                role="listitem"
                class="argus-ep-qrow is-${a.severity} ${judged ? `is-judged ${VERDICT_TONE[a.items[0]!.verdict ?? 'duplicate']}` : ''}"
                aria-current=${here(a) ? 'true' : 'false'}
                @click=${() => openArgusEpisode(this.firstOf(a).id)}
              >
                <span class="argus-ep-qdot" aria-hidden="true"></span>
                <span class="argus-ep-qtext"><b>${a.ruleName}</b><span>${sub}</span></span>
              </button>`;
            })
          : html`<p class="argus-lfaint argus-ep-queue-note">No open alerts.</p>`}
      </div>`;
    if (this.pinned) return html`<aside class="argus-ep-queue" aria-label="Open alerts">${list}</aside>`;
    return html`<div class="argus-ep-qwrap">
      <aside class="argus-ep-queue is-folded" aria-label="Open alerts - hover to open">
        <span class="argus-ep-qcount" title=${`${waiting} open alerts`}>${waiting}</span>
        ${alerts.map(
          (a) => html`<button
            type="button"
            class="argus-ep-qdot is-${a.severity} ${done(a) ? `is-judged ${VERDICT_TONE[a.items[0]!.verdict ?? 'duplicate']}` : ''}"
            aria-current=${here(a) ? 'true' : 'false'}
            title=${`${a.ruleName} · ${a.items.length} ${a.noun}${a.items.length === 1 ? '' : 's'}`}
            @click=${() => openArgusEpisode(this.firstOf(a).id)}
          ></button>`,
        )}
      </aside>
      <div class="argus-ep-queue argus-ep-qpanel">${list}</div>
    </div>`;
  }

  /** Who has a ship: a dashed Assign to me while nobody does, a name once someone does. */
  private ownerCell(i: ArgusInboxItem, may: boolean): TemplateResult {
    const me = myUserId.value;
    const mine = i.owner_id !== null && i.owner_id === me;
    if (!i.owner_id) {
      return may
        ? html`<button type="button" class="argus-ep-cellbtn is-assign" ?disabled=${this.locked} title=${`Put your name on ${i.entity_name}`} @click=${(ev: Event) => {
            ev.stopPropagation();
            void this.quickAct([i.id], () => takeArgusEpisode(i.id, true), { owner_id: me, owner_name: myName() });
          }}>${ICON_PERSON}Assign to me</button>`
        : html`<span class="argus-lfaint">-</span>`;
    }
    return html`<span class="argus-ep-who" title=${mine ? 'Assigned to you' : `Assigned to ${i.owner_name ?? 'someone'}`}>
      <span class="argus-ep-av ${mine ? 'is-you' : ''}" aria-hidden="true">${initials(i.owner_name)}</span>
      ${mine ? 'You' : shortName(i.owner_name)}
      ${may
        ? html`<button type="button" class="argus-ep-undo" ?disabled=${this.locked} @click=${(ev: Event) => {
            ev.stopPropagation();
            void this.quickAct([i.id], () => takeArgusEpisode(i.id, !mine), mine ? { owner_id: null, owner_name: null } : { owner_id: me, owner_name: myName() });
          }}>${mine ? 'Unassign' : 'Take over'}</button>`
        : nothing}
    </span>`;
  }

  /** Whether the team has seen a ship: a dashed Acknowledge, or who did and when. */
  private ackCell(i: ArgusInboxItem, may: boolean): TemplateResult {
    if (i.mark === 'seen' || i.mark === 'dismissed') {
      return html`<span class="argus-ep-ackd ${i.mark === 'dismissed' ? 'is-dismissed' : ''}">
        ${i.mark === 'seen' ? ICON_TICK : nothing}${i.mark === 'seen' ? shortName(i.marked_by_name) : 'dismissed'}
        ${i.marked_at ? html`<small>${ago(i.marked_at)}</small>` : nothing}
        ${may
          ? html`<button type="button" class="argus-ep-undo" ?disabled=${this.locked} @click=${(ev: Event) => {
              ev.stopPropagation();
              void this.quickAct([i.id], () => markArgusEpisodes([i.id], null), { mark: null, marked_by_name: null, marked_at: null });
            }}>Undo</button>`
          : nothing}
      </span>`;
    }
    if (!may || i.state !== 'open') return html`<span class="argus-lfaint">-</span>`;
    return html`<button type="button" class="argus-ep-cellbtn is-ack" ?disabled=${this.locked} title="The glow stops for everyone; it stays until it gets a verdict or ends" @click=${(ev: Event) => {
      ev.stopPropagation();
      void this.quickAct([i.id], () => markArgusEpisodes([i.id], 'seen'), { mark: 'seen', marked_by_name: myName(), marked_at: new Date().toISOString() });
    }}>${ICON_TICK}Acknowledge</button>`;
  }

  /**
   * The Ships zone's head buttons. With nothing ticked they act on every ship
   * still under review ("Assign all 6 to me"); with ticks, on the ticked ones.
   * Shown when the zone is folded too, so "all" is one click from anywhere.
   */
  private shipsTools(alert: ArgusAlert, live: ArgusInboxItem[]): TemplateResult | typeof nothing {
    if (!this.mayJudge() || !alert.mayHandle) return nothing;
    const me = myUserId.value;
    const picked = this.shipItems(alert).filter((i) => this.selected.has(i.id));
    if (picked.length) {
      const ids = picked.map((i) => i.id);
      const n = picked.length;
      const allMine = picked.every((i) => i.owner_id !== null && i.owner_id === me);
      const allSeen = picked.every((i) => i.mark === 'seen');
      const allMuted = picked.every((i) => i.snoozed_until);
      const anyOpen = picked.some((i) => i.state === 'open');
      return html`<span class="argus-ep-sel">${n} selected</span>
        <button type="button" class="argus-ep-qa is-many" ?disabled=${this.locked} @click=${() => void this.quickAct(ids, () => takeArgusEpisodes(ids, !allMine), allMine ? { owner_id: null, owner_name: null } : { owner_id: me, owner_name: myName() })}>
          ${ICON_PERSON}${allMine ? `Unassign ${n}` : `Assign ${n} to me`}
        </button>
        <button type="button" class="argus-ep-qa is-many" ?disabled=${this.locked || (!allSeen && !anyOpen)} @click=${() => void this.quickAct(ids, () => markArgusEpisodes(ids, allSeen ? null : 'seen'), allSeen ? { mark: null, marked_by_name: null, marked_at: null } : { mark: 'seen', marked_by_name: myName(), marked_at: new Date().toISOString() })}>
          ${ICON_TICK}${allSeen ? 'Undo acknowledge' : `Acknowledge ${n}`}
        </button>
        ${allMuted
          ? html`<button type="button" class="argus-ep-qa is-ghost" ?disabled=${this.locked} @click=${() => void this.quickAct(ids, () => snoozeArgusEpisodes(ids, null), { snoozed_until: null })}>Unmute ${n}</button>`
          : this.muteButton(
              `Mute ${n}…`,
              'Keep this rule out of the Inbox and the count on these ships for a while. The watcher keeps recording.',
              this.bulkSnoozeOpen,
              () => (this.bulkSnoozeOpen = !this.bulkSnoozeOpen),
              (days) => {
                this.bulkSnoozeOpen = false;
                void this.quickAct(ids, () => snoozeArgusEpisodes(ids, days));
              },
            )}
        <button type="button" class="argus-lmore argus-ep-clear" @click=${() => (this.selected = new Set())}>Clear</button>`;
    }
    const notMine = live.filter((i) => !(i.owner_id !== null && i.owner_id === me));
    const toAck = live.filter((i) => i.state === 'open' && i.mark !== 'seen' && i.mark !== 'dismissed');
    const n = live.length;
    return html`${notMine.length
        ? html`<button type="button" class="argus-ep-qa is-assign" ?disabled=${this.locked} title="Put your name on every ship still under review" @click=${() => this.assignAll(live)}>
            ${ICON_PERSON}Assign all ${n} to me
          </button>`
        : html`<span class="argus-ep-qa is-done">${ICON_PERSON}${n === 1 ? 'It is yours' : `All ${n} are yours`}</span>`}
      ${toAck.length
        ? html`<button type="button" class="argus-ep-qa is-ack" ?disabled=${this.locked} title="Say the team has seen every ship still open" @click=${() => void this.quickAct(toAck.map((i) => i.id), () => markArgusEpisodes(toAck.map((i) => i.id), 'seen'), { mark: 'seen', marked_by_name: myName(), marked_at: new Date().toISOString() })}>
            ${ICON_TICK}Acknowledge all ${toAck.length}
          </button>`
        : html`<span class="argus-ep-qa is-done">${ICON_TICK}All acknowledged</span>`}`;
  }

  /** Assign all to me: straight away, unless a colleague has some of them - then ask. */
  private assignAll(live: ArgusInboxItem[]): void {
    const me = myUserId.value;
    if (live.some((i) => i.owner_id && i.owner_id !== me)) {
      this.askTakeAll = true;
      return;
    }
    const free = live.filter((i) => !i.owner_id).map((i) => i.id);
    void this.quickAct(free, () => takeArgusEpisodes(free, true), { owner_id: me, owner_name: myName() });
  }

  /** The question Assign all to me asks when a colleague has some of the ships. */
  private renderTakeAllAsk(live: ArgusInboxItem[]): TemplateResult | typeof nothing {
    if (!this.askTakeAll) return nothing;
    const me = myUserId.value;
    const theirs = live.filter((i) => i.owner_id && i.owner_id !== me);
    const free = live.filter((i) => !i.owner_id);
    const notMine = live.filter((i) => i.owner_id !== me);
    const names = [...new Set(theirs.map((i) => shortName(i.owner_name)))].join(' and ');
    const run = (ids: number[]): void => {
      this.askTakeAll = false;
      void this.quickAct(ids, () => takeArgusEpisodes(ids, true), { owner_id: me, owner_name: myName() });
    };
    return html`<div class="argus-ep-ask" role="alert">
      <span><b>${theirs.length} ${theirs.length === 1 ? 'ship is' : 'ships are'} assigned to ${names}.</b>
        ${free.length ? `Take ${theirs.length === 1 ? 'it' : 'them'} too, or only the ${free.length} free?` : `Take ${theirs.length === 1 ? 'it' : 'them'} over?`}</span>
      ${free.length ? html`<button type="button" class="argus-ep-qa is-assign" @click=${() => run(free.map((i) => i.id))}>Only the ${free.length} free</button>` : nothing}
      <button type="button" class="argus-ep-qa is-assign" @click=${() => run(notMine.map((i) => i.id))}>Take all ${notMine.length}</button>
      <button type="button" class="argus-lmore" @click=${() => (this.askTakeAll = false)}>Cancel</button>
    </div>`;
  }

  /**
   * The alert's ships (layout B, round 3, 2026-10-22): a docked zone under the
   * head. A row's name looks at that ship; its tick selects it for the head's
   * buttons and Your call. Assign and Acknowledge sit in the column that shows
   * their answer: a dashed button where nothing is done, the person once it
   * is. Mute is in the row's ··· menu. An alert on one ship gets one line.
   */
  private renderShips(d: ArgusEpisodeDetail): TemplateResult | typeof nothing {
    const alert = this.currentAlert();
    if (!alert || alert.items.length < 2) return this.renderHandling(d);
    const may = this.mayJudge() && alert.mayHandle;
    const me = myUserId.value;
    const items = this.shipItems(alert);
    const live = items.filter((i) => !concluded(i));
    const ticked = live.filter((i) => this.selected.has(i.id)).length;
    const summary = [
      `${live.length} under review`,
      `${live.filter((i) => i.owner_id !== null && i.owner_id === me).length} yours`,
      `${live.filter((i) => !i.owner_id).length} unassigned`,
      `${live.filter((i) => i.mark === 'seen').length} acknowledged`,
    ].join(' · ');
    // The firing bars share one window: the first start to the last reading.
    const endOf = (i: ArgusInboxItem): number => msOf(i.ended_at ?? i.last_at ?? i.started_at);
    const from = Math.min(...items.map((i) => msOf(i.started_at)));
    const to = Math.max(from + 60000, ...items.map(endOf));
    const pct = (t: number): number => (100 * (t - from)) / (to - from);
    const at = items.findIndex((i) => i.id === this.episodeId);
    const short = !this.allShips && items.length > SHIP_ROWS && at < SHIP_ROWS;
    const rows = short ? items.slice(0, SHIP_ROWS) : items;
    const table = html`<div class="argus-ep-stwrap">
      <table class="argus-ep-st" aria-label=${`${alert.items.length} ${alert.noun}s in this alert`}>
        <thead>
          <tr>
            ${may
              ? html`<th class="argus-ep-st-tick">
                  <input
                    type="checkbox"
                    aria-label=${`Select every ${alert.noun} under review`}
                    title="Select every ship still under review"
                    .checked=${live.length > 0 && ticked === live.length}
                    .indeterminate=${ticked > 0 && ticked < live.length}
                    ?disabled=${!live.length}
                    @change=${() => this.toggleAll()}
                  />
                </th>`
              : nothing}
            <th>${alert.noun === 'ship' ? 'Ship' : 'Entity'}</th>
            <th>Since</th>
            <th>Firing</th>
            <th>Assigned to</th>
            <th>Acknowledged</th>
            <th></th>
            ${may ? html`<th class="argus-ep-st-more"></th>` : nothing}
          </tr>
        </thead>
        <tbody>
          ${rows.map((i) => {
            const here = i.id === this.episodeId;
            const done = concluded(i);
            const tone = i.verdict ? VERDICT_TONE[i.verdict] : '';
            const left = pct(msOf(i.started_at));
            const width = Math.max(1.5, pct(endOf(i)) - left);
            return html`<tr
              class="${here ? 'is-here' : ''} ${done ? 'is-judged' : ''} ${this.selected.has(i.id) ? 'is-ticked' : ''} ${i.snoozed_until ? 'is-muted' : ''}"
              aria-current=${here ? 'true' : 'false'}
              @click=${() => !here && openArgusEpisode(i.id)}
            >
              ${may
                ? html`<td class="argus-ep-st-tick" @click=${(e: Event) => e.stopPropagation()}>
                    ${done
                      ? nothing
                      : html`<input
                          type="checkbox"
                          aria-label=${`Select ${i.entity_name}`}
                          title=${here ? 'Select (X)' : 'Select'}
                          .checked=${this.selected.has(i.id)}
                          @change=${() => this.toggle(i.id)}
                        />`}
                  </td>`
                : nothing}
              <td class="argus-ep-st-name">
                <button type="button" class="argus-ep-st-open" title=${`Look at ${i.entity_name}`} @click=${(e: Event) => {
                  e.stopPropagation();
                  if (!here) openArgusEpisode(i.id);
                }}>
                  <span class="argus-ep-ship-dot ${tone}" style=${i.verdict ? '' : `--tone: rgb(var(--glow-${alert.severity}))`} aria-hidden="true"></span>
                  <span class="argus-ep-st-nm">${i.entity_name}</span>
                  ${here ? html`<span class="argus-ep-inview">In view</span>` : nothing}
                </button>
              </td>
              <td class="argus-ep-st-since">${when(i.started_at)}</td>
              <td>
                <span class="argus-ep-fire" title=${`${when(i.started_at)} to ${i.ended_at ? when(i.ended_at) : 'now'}`}>
                  <span class="argus-ep-fire-track"><span class="argus-ep-fire-bar" style=${`left:${left}%;width:${width}%`}></span></span>
                  <span class="argus-ep-st-num">${shortSpan(i.started_at, i.ended_at ?? i.last_at)}</span>
                </span>
              </td>
              ${done
                ? html`<td colspan="2"><span class="argus-ep-st-verdict ${tone}">${verdictWords(i)}</span></td>`
                : html`<td>${this.ownerCell(i, may)}</td>
                    <td>${this.ackCell(i, may)}</td>`}
              <td class="argus-ep-st-note">
                ${i.snoozed_until ? html`<span class="argus-lfaint">muted until ${when(i.snoozed_until)}</span>` : nothing}
              </td>
              ${may
                ? html`<td class="argus-ep-st-more" @click=${(e: Event) => e.stopPropagation()}>${done ? nothing : this.rowMenuFor(i)}</td>`
                : nothing}
            </tr>`;
          })}
          ${short
            ? html`<tr class="argus-ep-st-showmore"><td colspan="8">
                <button type="button" class="argus-lmore" @click=${() => (this.allShips = true)}>Show ${alert.items.length - SHIP_ROWS} more ${alert.noun}s</button>
              </td></tr>`
            : nothing}
        </tbody>
      </table>
    </div>`;
    return this.zone('ships', alert.noun === 'ship' ? 'Ships' : 'Entities', summary, html`${this.renderTakeAllAsk(live)}${table}`, {
      docked: true,
      keepSummary: true,
      alwaysTools: true,
      tools: this.shipsTools(alert, live),
      cls: this.selected.size && ticked ? 'is-ticking' : '',
    });
  }

  /** A row's ··· menu: Mute for a few days, or Unmute. */
  private rowMenuFor(i: ArgusInboxItem): TemplateResult {
    const open = this.rowMenu === i.id;
    return html`<span class="argus-ep-snooze">
      <button type="button" class="argus-ep-more" aria-label=${`More for ${i.entity_name}`} aria-expanded=${open ? 'true' : 'false'} title="Mute" @click=${() => (this.rowMenu = open ? null : i.id)}>···</button>
      ${open
        ? html`<span class="cat-menu argus-ep-snooze-menu is-right" role="menu">
            ${i.snoozed_until
              ? html`<button class="cat-menu-item" role="menuitem" type="button" @click=${() => {
                  this.rowMenu = null;
                  void this.quickAct([i.id], () => snoozeArgusEpisode(i.id, null), { snoozed_until: null });
                }}>Unmute on ${i.entity_name}</button>`
              : (this.detail?.snooze_days ?? []).map(
                  (n) => html`<button class="cat-menu-item" role="menuitem" type="button" @click=${() => {
                    this.rowMenu = null;
                    void this.quickAct([i.id], () => snoozeArgusEpisode(i.id, n));
                  }}>Mute on ${i.entity_name} for ${n === 1 ? '1 day' : `${n} days`}</button>`,
                )}
          </span>`
        : nothing}
    </span>`;
  }

  private renderRule(d: ArgusEpisodeDetail): TemplateResult {
    const r = d.rule;
    return html`<div class="argus-ep-rulebody">
      <p class="argus-ep-rule-name">${r.name} <span class="argus-chip is-${r.status}">${r.status}</span></p>
      ${r.description ? html`<p class="argus-lfaint">${r.description}</p>` : nothing}
      <dl class="argus-ep-facts">
        <div><dt>Version</dt><dd>${r.version}${r.found_this_version ? '' : html` <span class="argus-lfaint">(found by ${d.episode.version})</span>`}</dd></div>
        <div><dt>Fired, 30 days</dt><dd>${r.fired_30d}</dd></div>
        <div><dt>Not an issue</dt><dd>${r.not_an_issue_30d}</dd></div>
      </dl>
      ${r.judged_30d
        ? html`<div class="argus-ep-noise" title="Of the episodes concluded in 30 days, the share judged Not an issue">
            <span class="argus-lfaint">Noise: ${Math.round((100 * r.not_an_issue_30d) / r.judged_30d)}% of ${r.judged_30d} judged not an issue</span>
            <span class="argus-ep-noise-bar"><i style=${`width:${(100 * r.not_an_issue_30d) / r.judged_30d}%`}></i></span>
          </div>`
        : html`<p class="argus-lfaint">No verdicts on it in 30 days yet.</p>`}
      ${r.schedule_words
        ? html`<p class="argus-lfaint">
            ${r.schedule_words}${r.status === 'deployed' && r.next_run_at ? html` · next run ${until(r.next_run_at)}` : nothing}
          </p>`
        : nothing}
      <p class="argus-lfaint">${r.owner_name ?? ''}${r.group_name ? ` · ${r.group_name}` : ''}</p>
    </div>`;
  }

  override render(): TemplateResult {
    if (this.failed) {
      return html`<div class="argus-ep">
        <button type="button" class="argus-lmore" @click=${() => openArgusEpisode(null)}>← Back</button>
        <p class="argus-room-waiting"><span class="argus-room-waiting-label">Not here</span>${this.failed}</p>
      </div>`;
    }
    const d = this.detail;
    if (!d) return html`<div class="argus-ep"><p class="argus-room-waiting">Reading the episode…</p></div>`;
    const e = d.episode;
    const r = d.rule;
    const targets = this.targetIds();
    const lanes = d.timeline.by_rule;
    const fired = lanes.reduce((n, l) => n + l.bars.length, 0);
    const notes = d.notes ?? [];
    const lastNote = notes[notes.length - 1];
    const around = { episode: 'Around it', '7d': '7 days', '30d': '30 days' }[this.around];
    const noun = this.currentAlert()?.noun ?? 'ship';
    const forWhom = targets.length > 1
      ? html`<span class="argus-ep-zfor">for <b>${targets.length} selected ${noun}s</b></span>`
      : html`<span class="argus-ep-zfor">for <b>${e.entity_name}</b></span>`;
    return html`<div class="argus-ep argus-ep-a1 argus-ep-b ${this.pinned ? 'is-pinned' : ''}">
      ${this.renderQueue()}
      <div class="argus-ep-body ${this.stale ? 'is-stale' : ''}" aria-busy=${this.stale ? 'true' : 'false'}>
        <div class="argus-ep-progress" aria-hidden="true"></div>
        ${this.renderHead(d)}
        ${this.renderShips(d)}
        ${this.zone(
          'verdict',
          'Verdict',
          `${e.verdict ? verdictWords(e) : 'No verdict yet'} · ${around} · ${e.entity_name}`,
          html`<div class="argus-ep-band">
            <div class="argus-ep-band-data">${this.renderData(d)}</div>
            <div class="argus-ep-band-call">${this.renderJudge(d)}</div>
          </div>`,
          { tools: this.dataTools(d), docked: true, extra: forWhom },
        )}
        <div class="argus-ep-zones">
          ${this.zone(
            'timeline',
            'Timeline',
            `${fired} ${fired === 1 ? 'time' : 'times'} in ${d.timeline.days} days, on ${lanes.length} ${lanes.length === 1 ? noun : `${noun}s`}`,
            this.renderTimelineCard(d),
            { tools: this.timelineTools(d) },
          )}
          ${this.zone(
            'earlier',
            'Here before',
            d.earlier.length
              ? `On ${e.entity_name}: ${d.earlier.length} ${d.earlier.length === 1 ? 'time' : 'times'}, last ${when(d.earlier[0]!.started_at)}`
              : `The first time on ${e.entity_name} in ${d.timeline.days} days`,
            this.renderEarlier(d),
          )}
          ${this.zone(
            'notes',
            'Notes',
            lastNote ? `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}, last by ${lastNote.author_name}` : 'No notes yet',
            this.renderNotes(d),
          )}
          ${this.zone(
            'rule',
            'The rule',
            [r.status, `version ${r.version}`, `fired ${r.fired_30d} times in 30 days`, r.schedule_words].filter(Boolean).join(' · '),
            this.renderRule(d),
            { tools: html`<button type="button" class="argus-lmore" @click=${() => this.openRule()}>Open rule →</button>` },
          )}
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-episode-page': ArgusEpisodePage;
  }
}
